/* global BigInt */
/**
 * Link 20 D4 — paying a TIM order through the TimCheckout contract from a browser wallet.
 *
 * No React here: the steps, the checks and the error wording, with every chain/wallet/server call passed in
 * (`deps`), so the whole flow is unit-tested (checkoutPay.test.js). CheckoutPayCard.jsx wires it to wagmi.
 *
 * The flow (design doc D4):
 *   connect (browser extension) → switch to Base → check the card against the contract (its usdc()) →
 *   what's still owed ON-CHAIN (orderTotal − paidTotal: that's what payRemaining pulls) → USDC / USDbC / ETH
 *   balances → allowance → if short, approve exactly what's owed and wait for it, then re-read the allowance →
 *   simulate payRemaining(orderId, orderTotal) (shows the specific reason it would fail) → send it →
 *   tell TIM the tx hash (untrusted: it only pauses the card option) → wait for inclusion ("Payment sent").
 * The order is paid only when TIM says so (its scanner credits the payment); the card polls for that.
 */

export const BASE_CHAIN_ID = 8453;
export const USDC_DECIMALS = 6;
// Bridged USDC on Base. Not accepted: the contract only takes native USDC.
export const USDBC_ADDRESS = '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca';

export const erc20Abi = [
  { name: 'balanceOf', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }], outputs: [{ type: 'uint256' }] },
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }], outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'value', type: 'uint256' }], outputs: [{ type: 'bool' }] },
];

// TimCheckout: what the card calls, and its errors (so a simulation failure decodes to a name).
export const checkoutAbi = [
  { name: 'usdc', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  { name: 'paidTotal', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'orderId', type: 'bytes32' }], outputs: [{ type: 'uint256' }] },
  { name: 'payRemaining', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'orderId', type: 'bytes32' }, { name: 'orderTotal', type: 'uint256' }], outputs: [] },
  { type: 'error', name: 'ZeroOrderId', inputs: [] },
  { type: 'error', name: 'ZeroAmount', inputs: [] },
  { type: 'error', name: 'NothingOwed', inputs: [] },
  { type: 'error', name: 'TransferFailed', inputs: [] },
];

const ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const BYTES32 = /^0x[0-9a-f]{64}$/;

export const MESSAGES = {
  no_wallet: 'No browser wallet found. Install a wallet extension (MetaMask, Coinbase Wallet, Rabby…), then reload this page.',
  connect_rejected: 'Wallet connection cancelled — tap Pay to try again.',
  wrong_chain: 'Switch your wallet to the Base network to pay.',
  misconfigured: "This payment card doesn't match TIM's checkout contract, so it won't pay. Ask in the chat for help — nothing was taken.",
  nothing_owed: 'Nothing is owed on this order any more — TIM is confirming the payment.',
  usdbc: "Your wallet has USDbC (bridged USDC), not USDC. TIM takes native USDC on Base — swap USDbC for USDC first, then tap Pay.",
  no_gas: 'You need a little ETH on Base to pay the network fee (a few cents). Add some, then tap Pay.',
  approve_rejected: 'Approval cancelled — tap Pay to try again.',
  approve_failed: "The approval didn't go through on-chain (nothing was taken). Tap Pay to try again.",
  approve_short: "Your wallet approved less USDC than this payment needs. Tap Pay and approve again — keep the amount your wallet suggests.",
  pay_rejected: 'Payment cancelled — nothing was taken. Tap Pay to try again.',
  approved_not_paid: 'Approved — tap Pay to finish.',
  failed_onchain: 'The payment failed on-chain — nothing was taken. Tap Pay to try again.',
  cancelled_in_wallet: 'The payment was cancelled in your wallet — nothing was taken. Tap Pay to try again.',
  blacklisted: "This wallet is blocked by USDC's issuer (Circle), so it can't send USDC. Use another wallet, or pay by card.",
  paused: 'USDC transfers are paused by its issuer (Circle) right now. Try again later, or pay by card.',
  transfer_failed: 'The USDC transfer failed — nothing was taken. Tap Pay to try again.',
  low_allowance: "The approval isn't enough for this payment. Tap Pay to approve again.",
  unknown: 'Something went wrong — nothing was taken. Tap Pay to try again.',
  wallet_busy: 'Your wallet already has a request waiting — open it, finish or reject that, then tap Pay.',
  approve_cancelled: 'The approval was cancelled in your wallet — tap Pay to try again.',
  tim_unreachable: "Couldn't reach TIM to check this order — nothing was taken. Tap Pay to try again.",
};

// After an approval, a load-balanced RPC node can be a block behind: re-read this many times, a second apart,
// before concluding the approval really was short.
export const AFTER_APPROVE_RETRIES = 5;

/** USDC base units (bigint) → "0.06" / "1.50" / "0.000001": two decimals, more only when needed. */
export function formatUsdc(units) {
  const v = BigInt(units);
  const neg = v < 0n;
  const a = neg ? -v : v;
  const whole = a / 1_000_000n;
  let frac = (a % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '');
  if (frac.length < 2) frac = frac.padEnd(2, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

/** What payRemaining will pull: orderTotal − paidTotal, never negative. */
export function remainingUnits(orderTotal, paidTotal) {
  const r = BigInt(orderTotal) - BigInt(paidTotal);
  return r > 0n ? r : 0n;
}

/** Check a card's metadata before using it. Returns the parsed card, or null if it isn't usable. */
export function parseCard(meta) {
  if (!meta || meta.payment_type !== 'usdc_checkout') return null;
  const { order_id, order_id_bytes32, order_total_base_units, checkout_contract, usdc_contract, chain_id } = meta;
  if (!ADDRESS.test(checkout_contract || '') || !ADDRESS.test(usdc_contract || '')) return null;
  if (!BYTES32.test(order_id_bytes32 || '') || /^0x0+$/.test(order_id_bytes32)) return null;
  if (Number(chain_id) !== BASE_CHAIN_ID) return null;
  if (!/^[0-9]+$/.test(String(order_total_base_units || ''))) return null;
  const total = BigInt(order_total_base_units);
  if (total <= 0n) return null;
  // The on-chain id is the order id left-padded to 32 bytes — the card must say the same thing twice.
  if (!order_id || `0x${String(order_id).toLowerCase().padStart(64, '0')}` !== order_id_bytes32) return null;
  return {
    orderId: order_id,
    orderIdBytes32: order_id_bytes32,
    orderTotal: total,
    checkout: checkout_contract.toLowerCase(),
    usdc: usdc_contract.toLowerCase(),
  };
}

function errorText(err) {
  const parts = [];
  let e = err;
  for (let i = 0; e && i < 8; i += 1) {
    parts.push(e.name, e.shortMessage, e.details, e.message, e.reason, e.data?.errorName, e.errorName);
    e = e.cause;
  }
  return parts.filter(Boolean).join(' | ');
}

function hasCode(err, code) {
  let e = err;
  for (let i = 0; e && i < 8; i += 1) {
    if (e.code === code) return true;
    e = e.cause;
  }
  return false;
}

/** True when the person said no in their wallet. */
export function isRejection(err) {
  let e = err;
  for (let i = 0; e && i < 8; i += 1) {
    if (e.code === 4001 || e.name === 'UserRejectedRequestError') return true;
    e = e.cause;
  }
  return /user rejected|user denied|rejected the request|request rejected/i.test(errorText(err));
}

/** A wallet/contract error → the reason, in plain words. */
export function explainError(err) {
  if (isRejection(err)) return MESSAGES.pay_rejected;
  const t = errorText(err);
  if (hasCode(err, -32002) || /already pending|request.*pending/i.test(t)) return MESSAGES.wallet_busy;
  if (/NothingOwed/.test(t)) return MESSAGES.nothing_owed;
  if (/blacklist/i.test(t)) return MESSAGES.blacklisted;
  if (/paused/i.test(t)) return MESSAGES.paused;
  if (/insufficient funds|exceeds the balance of the account|gas required exceeds/i.test(t)) return MESSAGES.no_gas;
  if (/exceeds allowance|insufficient allowance/i.test(t)) return MESSAGES.low_allowance;
  if (/exceeds balance/i.test(t)) return 'Not enough USDC in this wallet for this payment.';
  if (/TransferFailed/.test(t)) return MESSAGES.transfer_failed;
  if (/chain ?mismatch|does not match the target chain|wrong network/i.test(t)) return MESSAGES.wrong_chain;
  return MESSAGES.unknown;
}

function lowBalance(have, need) {
  return `Not enough USDC: this wallet has ${formatUsdc(have)} USDC and the payment is ${formatUsdc(need)} USDC.`;
}

/**
 * Pay what's still owed on an order. Never throws: returns { outcome, message?, hash? }.
 *
 * outcome: 'sent'              the payment is on-chain (TIM confirms it shortly)
 *          'sent_unconfirmed'  sent, but inclusion couldn't be confirmed from here (TIM will see it if it landed)
 *          'nothing_owed'      the contract says nothing is left to pay
 *          'approved_not_paid' approved, then the payment prompt was declined
 *          'stopped'           anything else that stopped it (message says why; nothing was taken)
 *
 * deps (all async):
 *   connect() → account address        (throws { code: 'NO_WALLET' } when there's no browser wallet)
 *   switchToBase()
 *   read({ address, abi, functionName, args }) → value
 *   ethBalance(account) → bigint
 *   simulate({ address, abi, functionName, args, account }) → { request }
 *   write(request) → tx hash
 *   waitReceipt(hash, onReplaced) → { status: 'success' | 'reverted' }
 *   reportBroadcast(hash), reportFailed(hash)   (best effort: their errors are ignored)
 *   checkPayable() → { payable, reason }        TIM's view: may this order be paid in USDC right now?
 *                                               (asked before approving and again right before paying)
 *   sleep(ms)                                   (between re-reads after an approval)
 * onStep(step, info): 'connecting' | 'checking' | 'checked' ({ remaining, account }) | 'approve' | 'approving' | 'confirm' | 'sending'
 */
export async function payOrder(deps, card, onStep = () => {}) {
  // `error` (when there was one) is for the console: what the wallet or chain actually said.
  const stop = (message, error) => (error ? { outcome: 'stopped', message, error } : { outcome: 'stopped', message });
  let account;
  try {
    onStep('connecting');
    account = await deps.connect();
  } catch (e) {
    if (e?.code === 'NO_WALLET' || e?.name === 'ProviderNotFoundError') return stop(MESSAGES.no_wallet);
    return stop(isRejection(e) ? MESSAGES.connect_rejected : explainError(e), e);
  }
  try {
    await deps.switchToBase();
  } catch (e) {
    return stop(MESSAGES.wrong_chain, e);
  }

  const payableNow = async () => {
    let p;
    try {
      p = await deps.checkPayable();
    } catch (e) {
      return stop(MESSAGES.tim_unreachable, e);
    }
    return p && p.payable ? null : stop((p && p.reason) || MESSAGES.nothing_owed);
  };

  let remaining;
  try {
    onStep('checking');
    const notPayable = await payableNow();
    if (notPayable) return notPayable;
    const token = await deps.read({ address: card.checkout, abi: checkoutAbi, functionName: 'usdc' });
    if (String(token).toLowerCase() !== card.usdc) return stop(MESSAGES.misconfigured);
    const paid = await deps.read({ address: card.checkout, abi: checkoutAbi, functionName: 'paidTotal',
                                   args: [card.orderIdBytes32] });
    remaining = remainingUnits(card.orderTotal, paid);
    if (remaining === 0n) return { outcome: 'nothing_owed', message: MESSAGES.nothing_owed };

    const [usdcBal, usdbcBal, eth] = await Promise.all([
      deps.read({ address: card.usdc, abi: erc20Abi, functionName: 'balanceOf', args: [account] }),
      deps.read({ address: USDBC_ADDRESS, abi: erc20Abi, functionName: 'balanceOf', args: [account] })
        .catch(() => 0n),
      deps.ethBalance(account),
    ]);
    if (BigInt(usdcBal) < remaining) {
      return stop(BigInt(usdbcBal) >= remaining ? MESSAGES.usdbc : lowBalance(BigInt(usdcBal), remaining));
    }
    if (BigInt(eth) === 0n) return stop(MESSAGES.no_gas);
    onStep('checked', { remaining, account });
  } catch (e) {
    return stop(explainError(e), e);
  }

  // Approve exactly what's owed (decided 2026-10-26, D4.1: an unlimited approval to a contract wallets don't know
  // yet shows MetaMask's "High-risk approval" warning). payRemaining pulls exactly this, or less if someone else
  // paid meanwhile; an allowance that already covers it (e.g. "Approved — tap Pay to finish") isn't asked again.
  let approvedNow = false;
  try {
    const allowance = BigInt(await deps.read({ address: card.usdc, abi: erc20Abi, functionName: 'allowance',
                                               args: [account, card.checkout] }));
    if (allowance < remaining) {
      onStep('approve');
      let hash;
      try {
        const { request } = await deps.simulate({ address: card.usdc, abi: erc20Abi, functionName: 'approve',
                                                  args: [card.checkout, remaining], account });
        hash = await deps.write({ ...request, abi: erc20Abi });
      } catch (e) {
        return stop(isRejection(e) ? MESSAGES.approve_rejected : explainError(e), e);
      }
      onStep('approving', { hash });
      let approvalCancelled = false;
      const rc = await deps.waitReceipt(hash, (r) => { if (r?.reason !== 'repriced') approvalCancelled = true; });
      if (approvalCancelled) return stop(MESSAGES.approve_cancelled);
      if (rc?.status !== 'success') return stop(MESSAGES.approve_failed);
      let after = 0n;
      for (let i = 0; i < AFTER_APPROVE_RETRIES; i += 1) {
        if (i) await deps.sleep(1000);
        after = BigInt(await deps.read({ address: card.usdc, abi: erc20Abi, functionName: 'allowance',
                                         args: [account, card.checkout] }));
        if (after >= remaining) break;
      }
      if (after < remaining) return stop(MESSAGES.approve_short);
      approvedNow = true;
    }
  } catch (e) {
    return stop(explainError(e), e);
  }

  let hash;
  try {
    // Asked again right before paying: the order may have gone to card (another tab) since the first check.
    const notPayable = await payableNow();
    if (notPayable) return notPayable;
    onStep('confirm');
    const simulatePay = () => deps.simulate({ address: card.checkout, abi: checkoutAbi, functionName: 'payRemaining',
                                              args: [card.orderIdBytes32, card.orderTotal], account });
    let sim;
    for (let i = 0; ; i += 1) {
      try {
        sim = await simulatePay();
        break;
      } catch (e) {
        // Right after our own approval a lagging node may not see it yet: retry, then give the real reason.
        if (!approvedNow || i + 1 >= AFTER_APPROVE_RETRIES || explainError(e) !== MESSAGES.low_allowance) throw e;
        await deps.sleep(1000);
      }
    }
    // The full ABI, so a revert the wallet reports at send time (e.g. NothingOwed) still decodes.
    hash = await deps.write({ ...sim.request, abi: checkoutAbi });
  } catch (e) {
    if (isRejection(e)) {
      return approvedNow ? { outcome: 'approved_not_paid', message: MESSAGES.approved_not_paid }
                         : stop(MESSAGES.pay_rejected);
    }
    const message = explainError(e);
    if (message === MESSAGES.nothing_owed) return { outcome: 'nothing_owed', message };
    return stop(message, e);
  }

  await Promise.resolve(deps.reportBroadcast(hash)).catch(() => {});
  onStep('sending', { hash });

  let replacedBy = null;
  let cancelled = false;
  let rc;
  try {
    rc = await deps.waitReceipt(hash, (r) => {
      // Sped up: same payment, new hash. Cancelled/replaced: the payment never happens.
      if (r?.reason === 'repriced') replacedBy = r.transaction?.hash || null;
      else cancelled = true;
    });
  } catch (e) {
    return { outcome: 'sent_unconfirmed', hash };
  }
  if (cancelled) {
    await Promise.resolve(deps.reportFailed(hash)).catch(() => {});
    return stop(MESSAGES.cancelled_in_wallet);
  }
  if (replacedBy) {
    await Promise.resolve(deps.reportBroadcast(replacedBy)).catch(() => {});
    hash = replacedBy;
  }
  if (rc?.status !== 'success') {
    await Promise.resolve(deps.reportFailed(hash)).catch(() => {});
    return stop(MESSAGES.failed_onchain);
  }
  return { outcome: 'sent', hash };
}
